import { useRef, useEffect, useState, useCallback } from "react";
import { ContentItem } from "../content-card/types";
import { useTheme } from "@/components/ui/ThemeProvider";
import DraggableCard from "./freeform/DraggableCard";
import { useDragPosition } from "./freeform/useDragPosition";
import { useRouteContext } from "@/hooks/useRouteContext";
import { useSimplePositionPersistence } from "@/hooks/useSimplePositionPersistence";
import { useSelection } from "@/contexts/selection/SelectionContext";

interface FreeformViewProps {
  contentItems: ContentItem[];
  onContentClick: (id: string) => void;
  onDeleteContent: (id: string) => void;
  onEditContent?: (id: string, currentTitle: string) => void;
  onArchiveContent?: (id: string) => void;
  collectionColor?: string | null;
}

const FreeformView = ({ 
  contentItems, 
  onContentClick, 
  onDeleteContent, 
  onEditContent,
  onArchiveContent,
  collectionColor 
}: FreeformViewProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  
  // Selection context
  const { selectedIds, toggleSelection, selectAll, clearSelection } = useSelection();
  
  // Canvas state
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  
  // Position persistence - simplified approach
  const routeContext = useRouteContext();
  const { loadPositions, savePosition, deletePosition } = useSimplePositionPersistence(routeContext);
  const [savedPositions, setSavedPositions] = useState<Record<string, { x: number; y: number }>>({});
  
  // Canvas constants
  const GRID_SIZE = 50;
  const MIN_SCALE = 0.1;
  const MAX_SCALE = 5;
  const BOUNDARY_SIZE = 5000;
  
  // Content card layout constants
  const CARD_WIDTH = 320;
  const CARD_HEIGHT = 250;
  const CARD_SPACING_X = 360;
  const CARD_SPACING_Y = 290;
  const CARDS_PER_ROW = 4;
  
  // Get theme colors
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const backgroundColor = isDark ? '#101827' : '#F1F5F9';
  const gridColor = isDark ? '#000000' : '#ffffff';
  
  // Calculate card positions in a grid layout
  const calculateCardPosition = useCallback((index: number) => {
    const row = Math.floor(index / CARDS_PER_ROW);
    const col = index % CARDS_PER_ROW;
    
    const x = col * CARD_SPACING_X - (CARDS_PER_ROW * CARD_SPACING_X) / 2;
    const y = row * CARD_SPACING_Y - 200;
    
    return { x, y };
  }, []);

  // Load saved positions ONCE on mount or context change
  useEffect(() => {
    const loadSavedPositions = async () => {
      console.log('[FREEFORM VIEW] Loading saved positions...');
      const positions = await loadPositions();
      setSavedPositions(positions);
      console.log(`[FREEFORM VIEW] Loaded ${Object.keys(positions).length} saved positions`);
    };
    
    loadSavedPositions();
  }, [loadPositions]);

  // Initialize drag position management with immediate saving
  const {
    cardPositions,
    dragState,
    startDrag,
    updateDrag,
    endDrag,
    resetPositionsToGrid,
    alignPositionsToGrid
  } = useDragPosition({
    contentItems,
    calculateCardPosition,
    transform,
    canvasSize,
    savedPositions, // Pass loaded positions
    onPositionSaved: savePosition, // Use immediate save function
    gridSize: GRID_SIZE,
    selectedIds
  });

  // Handle selection for keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Select all on Cmd/Ctrl+A
      if ((e.metaKey || e.ctrlKey) && e.key === "a" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault();
        const allContentIds = contentItems.map(item => item.id);
        selectAll(allContentIds);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [contentItems, selectAll]);

  // Button handlers
  const handleReset = useCallback(async () => {
    console.log("Reset button clicked - resetting to grid layout");
    
    clearSelection();
    resetPositionsToGrid();
    
    // Clear all saved positions
    for (const item of contentItems) {
      await deletePosition(item.id);
    }
    
    setSavedPositions({});
    console.log("Reset complete - all cards returned to grid layout");
  }, [resetPositionsToGrid, contentItems, deletePosition, clearSelection]);

  const handleFrame = useCallback(() => {
    console.log("Frame button clicked - fitting all cards to view");
    
    if (contentItems.length === 0) return;
    
    // Calculate bounding box of all cards
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    
    contentItems.forEach((item) => {
      const position = cardPositions[item.id];
      if (position) {
        const cardLeft = position.x - CARD_WIDTH / 2;
        const cardRight = position.x + CARD_WIDTH / 2;
        const cardTop = position.y - CARD_HEIGHT / 2;
        const cardBottom = position.y + CARD_HEIGHT / 2;
        
        minX = Math.min(minX, cardLeft);
        maxX = Math.max(maxX, cardRight);
        minY = Math.min(minY, cardTop);
        maxY = Math.max(maxY, cardBottom);
      }
    });
    
    if (minX === Infinity) return; // No valid positions found
    
    // Add padding around the content
    const padding = 100;
    const contentWidth = maxX - minX + padding * 2;
    const contentHeight = maxY - minY + padding * 2;
    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;
    
    // Calculate scale to fit content in view
    const scaleX = canvasSize.width / contentWidth;
    const scaleY = canvasSize.height / contentHeight;
    const newScale = Math.min(scaleX, scaleY, MAX_SCALE);
    
    // Calculate transform to center content
    const newX = -contentCenterX * newScale;
    const newY = -contentCenterY * newScale;
    
    // Apply transform
    setTransform({
      x: Math.max(-BOUNDARY_SIZE, Math.min(BOUNDARY_SIZE, newX)),
      y: Math.max(-BOUNDARY_SIZE, Math.min(BOUNDARY_SIZE, newY)),
      scale: Math.max(MIN_SCALE, newScale)
    });
    
    console.log("Frame complete - all cards fitted to view");
  }, [contentItems, cardPositions, canvasSize, CARD_WIDTH, CARD_HEIGHT, MIN_SCALE, MAX_SCALE]);

  const handleAlignToGrid = useCallback(() => {
    console.log("Align to Grid button clicked - aligning cards to grid intersections");
    alignPositionsToGrid();
    console.log("Align to Grid initiated - positions will be saved automatically via batch save mechanism");
  }, [alignPositionsToGrid]);
  
  // Handle canvas resize
  const handleResize = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return;
    
    // Set canvas size with device pixel ratio for crisp rendering
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    
    // Scale context for high DPI
    ctx.scale(dpr, dpr);
    
    setCanvasSize({ width: rect.width, height: rect.height });
  }, []);
  
  // Draw grid
  const drawGrid = useCallback((ctx: CanvasRenderingContext2D) => {
    const { width, height } = canvasSize;
    const { x, y, scale } = transform;
    
    ctx.save();
    
    // Clear canvas with background color
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    
    // Apply transform
    ctx.translate(width / 2 + x, height / 2 + y);
    ctx.scale(scale, scale);
    
    // Draw grid
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 2 / scale;
    ctx.globalAlpha = 0.3;
    
    // Calculate visible area bounds
    const left = Math.floor((-width / 2 - x) / scale / GRID_SIZE) * GRID_SIZE;
    const right = Math.ceil((width / 2 - x) / scale / GRID_SIZE) * GRID_SIZE;
    const top = Math.floor((-height / 2 - y) / scale / GRID_SIZE) * GRID_SIZE;
    const bottom = Math.ceil((height / 2 - y) / scale / GRID_SIZE) * GRID_SIZE;
    
    ctx.beginPath();
    
    // Draw vertical lines
    for (let x = left; x <= right; x += GRID_SIZE) {
      if (x >= -BOUNDARY_SIZE && x <= BOUNDARY_SIZE) {
        ctx.moveTo(x, Math.max(top, -BOUNDARY_SIZE));
        ctx.lineTo(x, Math.min(bottom, BOUNDARY_SIZE));
      }
    }
    
    // Draw horizontal lines
    for (let y = top; y <= bottom; y += GRID_SIZE) {
      if (y >= -BOUNDARY_SIZE && y <= BOUNDARY_SIZE) {
        ctx.moveTo(Math.max(left, -BOUNDARY_SIZE), y);
        ctx.lineTo(Math.min(right, BOUNDARY_SIZE), y);
      }
    }
    
    ctx.stroke();
    
    // Draw center axes with theme-matching colors
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 3 / scale;
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(-BOUNDARY_SIZE, 0);
    ctx.lineTo(BOUNDARY_SIZE, 0);
    ctx.moveTo(0, -BOUNDARY_SIZE);
    ctx.lineTo(0, BOUNDARY_SIZE);
    ctx.stroke();
    
    ctx.restore();
  }, [canvasSize, transform, backgroundColor, gridColor]);
  
  // Render canvas
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    drawGrid(ctx);
  }, [drawGrid]);
  
  // Update content container transform
  const updateContentTransform = useCallback(() => {
    if (!contentContainerRef.current || !canvasSize.width || !canvasSize.height) return;
    
    const { x, y, scale } = transform;
    const centerX = canvasSize.width / 2;
    const centerY = canvasSize.height / 2;
    
    contentContainerRef.current.style.transform = 
      `translate(${centerX + x}px, ${centerY + y}px) scale(${scale})`;
  }, [transform, canvasSize]);
  
  // Mouse/touch event handlers for canvas panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start panning if clicking on a content card or if we're dragging a card
    const target = e.target as HTMLElement;
    if (target.closest('[data-content-card]') || dragState.isDragging) {
      return;
    }
    
    setIsPanning(true);
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  }, [dragState.isDragging]);
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Handle card dragging
    if (dragState.isDragging) {
      updateDrag(e);
      return;
    }

    // Handle canvas panning
    if (!isPanning) return;
    
    const deltaX = e.clientX - lastPanPoint.x;
    const deltaY = e.clientY - lastPanPoint.y;
    
    setTransform(prev => {
      const newX = Math.max(-BOUNDARY_SIZE, Math.min(BOUNDARY_SIZE, prev.x + deltaX));
      const newY = Math.max(-BOUNDARY_SIZE, Math.min(BOUNDARY_SIZE, prev.y + deltaY));
      return { ...prev, x: newX, y: newY };
    });
    
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  }, [isPanning, lastPanPoint, dragState.isDragging, updateDrag]);
  
  const handleMouseUp = useCallback(() => {
    if (dragState.isDragging) {
      endDrag();
    }
    setIsPanning(false);
  }, [dragState.isDragging, endDrag]);
  
  const handleWheel = useCallback((e: React.WheelEvent) => {
    // Don't zoom if dragging a card
    if (dragState.isDragging) return;
    
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left - rect.width / 2;
    const mouseY = e.clientY - rect.top - rect.height / 2;
    
    // Determine zoom direction and amount
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    
    setTransform(prev => {
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev.scale * zoomFactor));
      
      // Zoom towards mouse position
      const scaleChange = newScale / prev.scale;
      const newX = prev.x - (mouseX - prev.x) * (scaleChange - 1);
      const newY = prev.y - (mouseY - prev.y) * (scaleChange - 1);
      
      return {
        x: Math.max(-BOUNDARY_SIZE, Math.min(BOUNDARY_SIZE, newX)),
        y: Math.max(-BOUNDARY_SIZE, Math.min(BOUNDARY_SIZE, newY)),
        scale: newScale
      };
    });
  }, [dragState.isDragging]);
  
  // Setup canvas and event listeners
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);
  
  // Re-render when transform, theme, or canvas size changes
  useEffect(() => {
    render();
  }, [render, transform, theme, canvasSize]);
  
  // Update content transform when transform or canvas size changes
  useEffect(() => {
    updateContentTransform();
  }, [updateContentTransform]);
  
  // Mouse leave handler to stop panning and dragging
  const handleMouseLeave = useCallback(() => {
    setIsPanning(false);
    // Note: We don't end drag on mouse leave to allow dragging outside the container
  }, []);

  console.log("[FREEFORM VIEW] Rendered with", {
    contentItems: contentItems.length,
    canvasSize,
    transform,
    theme: isDark ? 'dark' : 'light',
    dragState: dragState.isDragging ? `dragging ${dragState.draggedCardIds.length} cards` : 'not dragging',
    context: `${routeContext.contextType}:${routeContext.contextId}`,
    savedPositions: Object.keys(savedPositions).length,
    selectedItems: selectedIds.length
  });

  // Get all content IDs for range selection
  const allContentIds = contentItems.map(item => item.id);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-background"
      style={{ backgroundColor }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onWheel={handleWheel}
    >
      {/* Background canvas for grid */}
      <canvas
        ref={canvasRef}
        className={`block ${dragState.isDragging ? "cursor-grabbing" : "cursor-grab"} ${isPanning ? "active:cursor-grabbing" : ""}`}
        style={{ 
          width: '100%', 
          height: '100%',
          backgroundColor 
        }}
      />
      
      {/* Freeform Control Buttons */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <button 
          onClick={handleReset}
          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Reset to Grid Layout"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M3 21v-5h5"/>
          </svg>
        </button>
        
        <button 
          onClick={handleFrame}
          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Frame All Cards"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" x2="2" y1="6" y2="6"/>
            <line x1="22" x2="2" y1="18" y2="18"/>
            <line x1="6" x2="6" y1="2" y2="22"/>
            <line x1="18" x2="18" y1="2" y2="22"/>
          </svg>
        </button>
        
        <button 
          onClick={handleAlignToGrid}
          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Align to Grid"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="3" rx="2"/>
            <path d="M9 9h6v6H9z"/>
            <path d="M3 9h18"/>
            <path d="M3 15h18"/>
            <path d="M9 3v18"/>
            <path d="M15 3v18"/>
          </svg>
        </button>
      </div>
      
      {/* Content container for cards */}
      <div
        ref={contentContainerRef}
        className="absolute top-0 left-0 pointer-events-none"
        style={{
          transformOrigin: '0 0',
          width: 0,
          height: 0
        }}
      >
        {contentItems.map((item) => {
          const position = cardPositions[item.id] || { x: 0, y: 0, zIndex: 1 };
          
          return (
            <DraggableCard
              key={item.id}
              item={item}
              position={position}
              dragState={dragState}
              onMouseDown={startDrag}
              onContentClick={onContentClick}
              onDeleteContent={onDeleteContent}
              onEditContent={onEditContent}
              onArchiveContent={onArchiveContent}
              allContentIds={allContentIds}
              cardWidth={CARD_WIDTH}
              cardHeight={CARD_HEIGHT}
              isSelected={selectedIds.includes(item.id)}
              onToggleSelection={toggleSelection}
            />
          );
        })}
      </div>
      
      {/* Debug info */}
      <div className="absolute top-2 left-2 bg-black/10 dark:bg-white/10 rounded px-2 py-1 text-xs text-foreground pointer-events-none">
        Scale: {transform.scale.toFixed(2)} | 
        X: {Math.round(transform.x)} | 
        Y: {Math.round(transform.y)} |
        Cards: {contentItems.length} |
        Theme: {isDark ? 'Dark' : 'Light'} |
        Saved: {Object.keys(savedPositions).length} |
        Selected: {selectedIds.length}
        {dragState.isDragging && ` | Dragging: ${dragState.draggedCardIds.length} cards`}
      </div>
    </div>
  );
};

export default FreeformView;
